/**
 * Design Tokens
 *
 * Single source of truth for brand colors, typography, spacing, and sizing. Tokens describe visual ROLES, not features.
 * Features reference tokens; tokens never reference features.
 *
 * All dimensional values are expressed in `rem`. Design specs arrive in `px` at the reference display . Use `remFromPx` to
 * translate at the token boundary.
 *
 * @see ADR-006: Central Theming
 * @see ADR-013: UI Scaling
 * @see UI/UX Design Guide
 */

// ---------------------------------------------------------------------------
// Scaling primitives
//
// The reference root font size is fixed at 16px. User-driven density scaling
// is applied via the `--ui-scale` CSS custom property, which multiplies the
// root font size at runtime. Because `rem` values inherit the scaled root
// font size, all tokens below automatically respect density and user browser
// zoom without per-token arithmetic.
// ---------------------------------------------------------------------------
export const ROOT_FONT_SIZE_PX = 16;

/** CSS custom property name carrying the global density scale factor. */
export const UI_SCALE_CSS_VAR = '--ui-scale';

/** Default value for `--ui-scale`. Corresponds to "Default" density. */
export const UI_SCALE_DEFAULT = '1';

/**
 * Convert a pixel value from a design spec into a `rem` string for consumption in CSS-in-JS or the MUI theme.
 *
 * Values arrive in px at the reference display; output is px / 16 in rem.
 *
 *     remFromPx(32) -> '2rem'
 *     remFromPx(10) -> '0.625rem'
 */
export function remFromPx(px: number): `${number}rem` {
    const rem = px / ROOT_FONT_SIZE_PX;
    return `${parseFloat(rem.toFixed(6))}rem` as `${number}rem`;
}

/**
 * Global root CSS to be injected once at application bootstrap. Establishes the `--ui-scale` custom property and wires the
 * scaled root font size.
 */
export const ROOT_CSS = {
    ':root': {
        [UI_SCALE_CSS_VAR]: UI_SCALE_DEFAULT,
        fontSize: `calc(${ROOT_FONT_SIZE_PX}px * var(${UI_SCALE_CSS_VAR}))`,
    },
} as const;

// ---------------------------------------------------------------------------
// Brand colors (Trade Dress)
//
// Purple, dark blue, light blue, and orange serve as trademarks for
// the PARATUS brand family.
//
//   - Purple is the dominant accent color.
//   - Dark blue, light blue, and orange are secondary accent colors;
//     orange is used sparingly given its visual strength.
//
// Keys are percentages from the brand sheet. `100` is the full-strength
// brand value; smaller numbers are progressively lighter tints (base
// blended with white at the corresponding opacity). Values are sourced
// directly from the PARATUS brand handoff and must not be hand-edited.
// ---------------------------------------------------------------------------
export const brand = {
    purple: {
        100: '#46388a',
        90: '#584c96',
        80: '#6b60a1',
        70: '#7d74ad',
        60: '#9088b9',
        50: '#a29bc4',
        40: '#b5afd0',
        30: '#c7c3dc',
        20: '#dad7e8',
        10: '#ecebf3',
        5: '#f6f5f9',
    },
    darkBlue: {
        100: '#0f145c',
        90: '#272b6c',
        80: '#3f437d',
        70: '#575a8d',
        60: '#6f729d',
        50: '#8789ad',
        40: '#9fa1be',
        30: '#b7b8ce',
        20: '#cfd0de',
        10: '#e7e7ef',
        5: '#f3f3f7',
    },
    lightBlue: {
        100: '#718fb7',
        90: '#7f9abe',
        80: '#8da5c5',
        70: '#9cb1cd',
        60: '#aabcd4',
        50: '#b8c7db',
        40: '#c6d2e2',
        30: '#d4dde9',
        20: '#e3e9f1',
        10: '#f1f4f8',
        5: '#f8f9fb',
    },
    orange: {
        100: '#ed6a1f',
        90: '#ef7936',
        80: '#f1884c',
        70: '#f29763',
        60: '#f4a679',
        50: '#f6b48f',
        40: '#f8c3a5',
        30: '#fad2bb',
        20: '#fbe1d2',
        10: '#fdf0e8',
        5: '#fef7f4',
    },
} as const;

// ---------------------------------------------------------------------------
// Dark-mode extended palette
//
// Engineered shade ramps and neutrals for dark-mode surfaces. These are
// NOT brand tints (blending with white); they are darkening sequences
// from brand anchors toward black, plus a neutral gray ramp.
//
// Structure:
//   - `purple`:     shade ramp anchored at brand.purple[100],
//                   darkening in 10%-equivalent steps.
//   - `purpleSoft`: shade ramp anchored at brand.purple[80] ,
//                   for surfaces that want a less-saturated dark base.
//   - `neutral`:    grayscale ramp (100 = white, 50 = mid-gray).
// ---------------------------------------------------------------------------
export const darkMode = {
    purple: {
        10: '#312666',
        20: '#251d4d',
        30: '#1a1533',
        40: '#0e0a1a',
        50: '#07050d',
    },
    purpleSoft: {
        10: '#554c80',
        20: '#443d66',
        30: '#332e4d',
        40: '#221e33',
        50: '#110f1a',
    },
    neutral: {
        100: '#ffffff',
        90: '#f2f2f2',
        80: '#e6e6e6',
        70: '#cccccc',
        60: '#b3b3b3',
        50: '#999999',
    },
} as const;

// ---------------------------------------------------------------------------
// Border colors
// ---------------------------------------------------------------------------
export const border = {
    /** General-purpose border — internal dividers, cards, sections */
    default: '#757575',
} as const;

// ---------------------------------------------------------------------------
// Accents
// ---------------------------------------------------------------------------
export const accents = {
    primary: '#504b6b',
    secondary: '#718fb7',
};

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
export const buttons = {
    reportEditor: {
        saveDraft: '#D8781F',
        approve: '#554C80',
    },
};

// ---------------------------------------------------------------------------
// Surface colors
//
// Named by depth/elevation. "overlay" is the deepest surface (menus, popovers,
// drawer backgrounds). Base and raised are covered by MUI's
// palette.background.default / paper.
// ---------------------------------------------------------------------------
export const surface = {
    /** Deep surfaces: menus, popovers, drawer backgrounds */
    overlay: '#05061A',
} as const;

// ---------------------------------------------------------------------------
// Debug helpers
// ---------------------------------------------------------------------------
export const debug = {
    red: '#FF0000',
};

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const typography = {
    fontFamily: "'Inter', Arial, sans-serif",
    weights: {
        regular: 400,
        semiBold: 600,
    },

    /**
     * Type scale per the UI/UX Guide. Values are rem, translated from designer-facing px at reference. `caption` is the
     * floor — do not introduce smaller sizes without a design review.
     */
    sizes: {
        caption: remFromPx(10), // 0.625rem — dense labels; floor
        bodySmall: remFromPx(12), // 0.75rem  — metadata, secondary
        body: remFromPx(14), // 0.875rem — default UI text
        bodyLarge: remFromPx(16), // 1rem     — tooltips, dialog body
        heading: remFromPx(18), // 1.125rem — panel/dialog headings
    },
} as const;

// ---------------------------------------------------------------------------
// Spacing scale
//
// Keys are the designer-facing px value at reference; values are the rem
// equivalent. When the designer writes "16px margin", the developer writes
// `spacing[16]`.
//
// Extend only when a design spec introduces a value not already present.
// ---------------------------------------------------------------------------
/**
 * Spacing scale kept as part of the theme token API even while unconsumed
 *
 * @public
 */
export const spacing = {
    0: '0',
    2: remFromPx(2),
    4: remFromPx(4),
    8: remFromPx(8),
    12: remFromPx(12),
    16: remFromPx(16),
    20: remFromPx(20),
    24: remFromPx(24),
    32: remFromPx(32),
    40: remFromPx(40),
    48: remFromPx(48),
} as const;

// ---------------------------------------------------------------------------
// Interactive target and icon sizes
//
// Target sizes define the clickable area. An icon inside the target is
// typically smaller than the target itself.
// ---------------------------------------------------------------------------
export const size = {
    target: {
        /** Default click target (40px at reference). */
        default: remFromPx(40),

        /**
         * Dense floor (32px at reference) for grouped toolbar contexts with clear visual separation between adjacent
         * targets.
         */
        dense: remFromPx(32),

        /** Compact target (28px). Use only within tightly grouped secondary toolbar. */
        compact: remFromPx(28),
    },
    icon: {
        small: remFromPx(16),
        medium: remFromPx(20),
        large: remFromPx(24),
    },
} as const;

// ---------------------------------------------------------------------------
// Control sizes
//
// A "control" is an interactive toolbar surface (icon button, toggle, menu
// launcher). Each preset bundles the clickable target box with the icon that
// sits inside it, so a control's box and glyph scale together from one source.
// ---------------------------------------------------------------------------
export const control = {
    /** Standard toolbar control: 32px target, 24px icon. */
    default: {
        target: size.target.dense,
        icon: size.icon.large,
    },

    /** Compact control for a tightly grouped secondary toolbar: 28px target, 16px icon. */
    compact: {
        target: size.target.compact,
        icon: size.icon.small,
    },
} as const;

export type ControlSize = keyof typeof control;
